import { useEffect } from 'react';
import Lenis from 'lenis';

/**
 * Module-level singleton — other utilities (e.g. ScrollToTop on route change)
 * can call `getLenis()?.scrollTo(...)` without having to subscribe to React
 * state. Lenis is owned by useSmoothScroll, but exposed so we can drive it
 * imperatively from route transitions.
 */
let lenisInstance: Lenis | null = null;
export function getLenis(): Lenis | null {
  return lenisInstance;
}

/**
 * Initialise Lenis smooth scrolling once at the app shell level.
 * Skips initialisation when the user prefers reduced motion.
 */
export function useSmoothScroll(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisInstance = lenis;

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisInstance = null;
    };
  }, []);
}
